import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Sparkles, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

/**
 * In-app Templates gallery. One click creates a live record in the workspace
 * with template metadata + download link embedded.
 */
export default function AppTemplates() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/templates").then((r) => setItems(r.data.templates));
  }, []);

  const activateTemplate = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/templates/use", { template_id: id });
      toast.success("Template added to your workspace");
      nav(`/app/records/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setBusy(null);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="app-templates-page">
      <div>
        <div className="overline text-primary">/ TEMPLATE LIBRARY</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Spreadsheet Templates</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          10 battle-tested Lean, Quality, HR, and Manufacturing templates.
          Download the XLSX, or use it as a live record in your workspace with AI drafting.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-border">
        {items.map((t, i) => (
          <div
            key={t.id}
            data-testid={`app-template-${t.id}`}
            className={`p-6 bg-card border-border flex flex-col
              ${i % 3 !== 2 ? "lg:border-r" : ""}
              ${i % 2 !== 1 ? "md:border-r lg:border-r-0" : ""}
              border-b`}
          >
            <div className="flex items-start justify-between">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
              <span className="overline text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div className="mt-4 font-display font-bold text-lg leading-tight">{t.title}</div>
            <div className="overline text-primary mt-2">{t.category}</div>
            <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">
              {t.description}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <Button
                onClick={() => activateTemplate(t.id)}
                disabled={busy === t.id}
                className="w-full rounded-sm bg-primary hover:bg-primary/90"
                data-testid={`use-${t.id}`}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {busy === t.id ? "Adding…" : "Use in workspace"}
              </Button>
              <a href={`${API}/templates/${t.id}/download`} target="_blank" rel="noreferrer" data-testid={`app-download-${t.id}`}>
                <Button variant="outline" className="w-full rounded-sm" size="sm">
                  <Download className="w-3.5 h-3.5 mr-2" /> Download XLSX
                </Button>
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
